import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';

// When this tenant's last token request failed. Without a memory of that, a tenant
// whose dialer cannot work (see below) re-requests a doomed token on every mount
// forever: measured 2026-08-27, 166 calls to twilio-token in 14 minutes, 112 of them
// 500s, all from one person sitting on lead pages. Backoff rather than a permanent
// block, so a genuinely transient failure still recovers on its own.
//
// sessionStorage rather than module scope or a ref: a ref dies with the remount this
// defends against, and Layout.jsx records module-level state reading back empty on
// this app, so a module Map may be wiped too. sessionStorage survives both.
const TOKEN_RETRY_BACKOFF_MS = 5 * 60 * 1000;
const tokenFailKey = (t) => `sfx_dialer_failed_at:${t}`;
const readTokenFailedAt = (t) => {
  if (!t) return 0;
  try { return Number(sessionStorage.getItem(tokenFailKey(t))) || 0; } catch { return 0; }
};
const writeTokenFailedAt = (t, ms) => {
  if (!t) return;
  try {
    if (ms === null) sessionStorage.removeItem(tokenFailKey(t));
    else sessionStorage.setItem(tokenFailKey(t), String(ms));
  } catch { /* private mode */ }
};

export default function useTwilioCall({ session, supabase }) {
  const [device, setDevice] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [call, setCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle | connecting | ringing | in-progress | ended
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const callStartTimeRef = useRef(null);
  const currentCallSidRef = useRef(null);
  const currentLeadRef = useRef(null);
  const currentPhoneRef = useRef(null);
  const callerIdRef = useRef(null); // tenant's Twilio number (from_number)
  const deviceRef = useRef(null);
  const isMountedRef = useRef(true);

  // The IDENTITY inside the session, not the session object. This effect used to
  // depend on `[session]`, and AuthContext replaces that object roughly 12 times a
  // minute (three setUser/setSession calls per loadUserInfo), so the dialer re-armed
  // and re-requested a token on every one of them. These are primitives, so the effect
  // now runs when the signed-in identity actually changes and not before.
  // The access token is deliberately NOT a dependency: it rotates hourly, the effect
  // reads the current one from localStorage (which AuthContext keeps up to date on
  // every refresh), and tearing down a live Twilio Device mid-call to re-arm it with
  // an identical identity would be worse than the staleness it prevents.
  const sessionUserId = session?.user?.id;
  const sessionTenantId =
    session?.user?.user_metadata?.tenant_id ||
    session?.user?.app_metadata?.tenant_id ||
    session?.user?.tenant_id;

  // Initialize Twilio Device
  useEffect(() => {
    isMountedRef.current = true;
    let twilioDevice = null;

    const initialize = async () => {
      try {
        const authToken = localStorage.getItem('auth_token') || session?.access_token;
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const userId = sessionUserId;
        const tenantId = sessionTenantId;

        if (!tenantId || !userId) {
          console.log('[SurFox Dialer] Waiting for session data');
          return;
        }

        // Recently failed for this tenant. Stay quiet rather than hammering an
        // endpoint we already know is refusing us, and surface the last reason.
        const failedAt = readTokenFailedAt(tenantId);
        if (failedAt && Date.now() - failedAt < TOKEN_RETRY_BACKOFF_MS) {
          if (isMountedRef.current) {
            setIsReady(false);
            setError('Dialer unavailable');
          }
          return;
        }

        setError(null);

        const response = await fetch(`${supabaseUrl}/functions/v1/twilio-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: userId, tenant_id: tenantId }),
        });

        if (!response.ok) throw new Error(`Token error (${response.status})`);

        const data = await response.json();
        if (!data.accessToken) throw new Error(data.error || 'No access token received');
        if (!data.accessToken.startsWith('ey')) throw new Error('Invalid token format');

        if (!isMountedRef.current) return;

        // Store the caller ID (tenant's outbound phone number)
        if (data.callerId) callerIdRef.current = data.callerId;

        twilioDevice = new Device(data.accessToken, {
          codecPreferences: ['opus', 'pcmu'],
          edge: 'ashburn',
          logLevel: 1,
        });

        twilioDevice.on('registered', () => {
          if (isMountedRef.current) {
            setIsReady(true);
            setError(null);
          }
        });

        twilioDevice.on('unregistered', () => {
          if (isMountedRef.current) setIsReady(false);
        });

        twilioDevice.on('error', (err) => {
          console.log('[SurFox Dialer] Device error:', err.code, err.message);
          if (isMountedRef.current) {
            let msg = err.message || 'Connection error';
            if (err.code === 31202) msg = 'JWT signature invalid';
            else if (err.code === 31204) msg = 'Token expired';
            else if (err.code === 31205) msg = 'Token invalid';
            setError(msg);
            setIsReady(false);
          }
        });

        twilioDevice.on('incoming', (incomingCall) => {
          incomingCall.accept();
          if (isMountedRef.current) {
            setCall(incomingCall);
            setCallStatus('in-progress');
          }
        });

        await twilioDevice.register();

        if (isMountedRef.current) {
          deviceRef.current = twilioDevice;
          setDevice(twilioDevice);
          setIsReady(true);
          // Armed successfully — clear any earlier backoff so a tenant that was
          // failing for a transient reason is not held down by a stale record.
          if (sessionTenantId) writeTokenFailedAt(sessionTenantId, null);
        }
      } catch (err) {
        console.log('[SurFox Dialer] Init error:', err);
        // Remember the failure so remounts back off instead of re-requesting.
        if (sessionTenantId) writeTokenFailedAt(sessionTenantId, Date.now());
        if (isMountedRef.current) {
          setError(err.message || 'Failed to initialize dialer');
          setIsReady(false);
        }
      }
    };

    if (sessionUserId) {
      initialize();
    }

    return () => {
      isMountedRef.current = false;
      if (twilioDevice) twilioDevice.destroy();
    };
  }, [sessionUserId, sessionTenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Call duration timer
  useEffect(() => {
    let interval = null;
    if (callStatus === 'in-progress') {
      interval = setInterval(() => setCallDuration(prev => prev + 1), 1000);
    } else if (callStatus === 'idle' || callStatus === 'ended') {
      setCallDuration(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [callStatus]);

  const makeCall = useCallback(async (phoneNumber, lead = null) => {
    const dev = deviceRef.current;
    if (!dev) {
      setError('Dialer not ready');
      return;
    }

    try {
      setCallStatus('connecting');
      setIsMuted(false);

      const params = { To: phoneNumber };
      if (lead?.id) params.lead_id = lead.id;

      const activeCall = await dev.connect({ params });

      activeCall.on('ringing', () => {
        if (isMountedRef.current) setCallStatus('ringing');
      });

      activeCall.on('accept', async () => {
        if (!isMountedRef.current) return;
        setCallStatus('in-progress');
        setCall(activeCall);

        callStartTimeRef.current = new Date();
        currentCallSidRef.current = activeCall.parameters?.CallSid || activeCall.outboundConnectionId || null;
        currentLeadRef.current = lead;
        currentPhoneRef.current = phoneNumber;

        // Audio routing
        const remoteStream = activeCall.getRemoteStream();
        if (remoteStream) {
          remoteStream.getAudioTracks().forEach(track => { track.enabled = true; });

          const existing = document.getElementById('surfox-remote-audio');
          if (existing) existing.remove();

          let selectedDeviceId = '';
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const outputs = devices.filter(d => d.kind === 'audiooutput');
            const poly = outputs.find(d => d.label.includes('Poly') && d.deviceId !== 'default' && d.deviceId !== 'communications');
            if (poly) {
              selectedDeviceId = poly.deviceId;
            } else {
              const real = outputs.find(d => d.deviceId !== 'default' && d.deviceId !== 'communications');
              if (real) selectedDeviceId = real.deviceId;
            }
          } catch (e) { /* ignore */ }

          const audioEl = document.createElement('audio');
          audioEl.id = 'surfox-remote-audio';
          audioEl.srcObject = remoteStream;
          audioEl.autoplay = true;
          audioEl.muted = false;
          audioEl.volume = 1.0;
          document.body.appendChild(audioEl);

          if (audioEl.setSinkId && selectedDeviceId) {
            try { await audioEl.setSinkId(selectedDeviceId); } catch (e) {
              try { await audioEl.setSinkId('default'); } catch (e2) { /* ignore */ }
            }
          }

          try { await audioEl.play(); } catch (e) { /* ignore */ }

          try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            if (audioCtx.state === 'suspended') audioCtx.resume();
            const source = audioCtx.createMediaStreamSource(remoteStream);
            source.connect(audioCtx.destination);
          } catch (e) { /* ignore */ }
        }
      });

      activeCall.on('disconnect', async () => {
        const endTime = new Date();
        const startTime = callStartTimeRef.current || new Date();
        const durationSeconds = Math.floor((endTime - startTime) / 1000);

        try {
          const fromNumber = callerIdRef.current;
          const toNumber = currentPhoneRef.current;
          // from_number and to_number are NOT NULL — skip insert if either is missing
          if (fromNumber && toNumber) {
            const callLog = {
              tenant_id: session?.user?.user_metadata?.tenant_id || session?.user?.app_metadata?.tenant_id,
              user_id: session?.user?.id,
              lead_id: currentLeadRef.current?.id || null,
              call_sid: currentCallSidRef.current,
              from_number: fromNumber,
              to_number: toNumber,
              direction: 'outbound',
              status: 'completed',
              duration_seconds: durationSeconds,
              started_at: startTime.toISOString(),
              ended_at: endTime.toISOString(),
            };
            const { error: logErr } = await supabase.from('call_logs').insert(callLog);
            if (logErr) console.log('[SurFox Dialer] Failed to log call:', logErr);
          } else {
            console.log('[SurFox Dialer] Skipping call_logs insert — missing from/to number');
          }
        } catch (e) {
          console.log('[SurFox Dialer] Error logging call:', e);
        }

        // Cleanup audio element
        const audioEl = document.getElementById('surfox-remote-audio');
        if (audioEl) audioEl.remove();

        callStartTimeRef.current = null;
        currentCallSidRef.current = null;
        currentLeadRef.current = null;
        currentPhoneRef.current = null;

        if (isMountedRef.current) {
          setCall(null);
          setCallStatus('ended');
        }
      });

      activeCall.on('error', (err) => {
        console.log('[SurFox Dialer] Call error:', err);
        if (isMountedRef.current) {
          setCall(null);
          setCallStatus('idle');
          setError(err.message || 'Call failed');
        }
      });

    } catch (err) {
      console.log('[SurFox Dialer] makeCall error:', err);
      setCallStatus('idle');
      setError(err.message);
    }
  }, [session, supabase]);

  const hangup = useCallback(() => {
    if (call) call.disconnect();
    setCall(null);
    setCallStatus('ended');
  }, [call]);

  const toggleMute = useCallback(() => {
    if (call) {
      const newState = !isMuted;
      call.mute(newState);
      setIsMuted(newState);
    }
  }, [call, isMuted]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    device, isReady, error,
    call, callStatus, callDuration, isMuted,
    makeCall, hangup, toggleMute, formatDuration,
  };
}
